import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Scaffold } from 'layouts';
import * as S from '../styles';
import { Paginator } from 'components/Shared/Pagination/Paginator';
import {
  ConfirmationDialog,
  type ConfirmationDialogRef,
} from 'components/Shared/ConfirmationDialog';
import { notify } from 'services/notifications';
import { OrderTypeEnum } from 'constants/Common';
import { useHoraCertaImport } from 'hooks/api/useHoraCertaImport';
import type {
  HoraCertaQuarantineDock,
  HoraCertaQuarantinePagination,
  HoraCertaQuarantineRecord,
} from 'contracts/HoraCertaImport';
import {
  buildDateFilterRange,
  getScheduledDate,
  parseTimeToMinutes,
} from './helpers';
import type {
  DateFilterKey,
  RowSelection,
  SavedOrder,
  SlotOption,
} from './types';
import { QuarantineHeader } from './components/QuarantineHeader';
import { QuarantineDateFilter } from './components/QuarantineDateFilter';
import { QuarantineTable } from './components/QuarantineTable';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

const getErrorMessage = (error: any): string => {
  const response = error?.response;
  if (response?.status >= 500) {
    return 'Ocorreu um erro interno. Tente novamente mais tarde.';
  }
  if (response?.data?.code === 'SLOT_UNAVAILABLE') {
    return 'Conflito de horario';
  }
  return (
    response?.data?.message ||
    error?.message ||
    'Não foi possível concluir a operação.'
  );
};

export const QuarantinePage: React.FC = () => {
  const {
    findQuarantine,
    findAvailability,
    confirmQuarantine,
    expirePastQuarantine,
    deleteQuarantineRecord,
  } = useHoraCertaImport();
  const confirmationDialogRef = useRef<ConfirmationDialogRef>(null);
  const selectionRef = useRef<Record<number, RowSelection>>({});

  const [records, setRecords] = useState<HoraCertaQuarantineRecord[]>([]);
  const [meta, setMeta] = useState<HoraCertaQuarantinePagination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpiring, setIsExpiring] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilterKey>('all');
  const [pageSize, setPageSize] = useState(10);
  const dateRange = useMemo(
    () => buildDateFilterRange(dateFilter),
    [dateFilter]
  );
  const [docksByRecord, setDocksByRecord] = useState<
    Record<number, HoraCertaQuarantineDock[]>
  >({});
  const [loadingDocks, setLoadingDocks] = useState<Record<number, boolean>>({});
  const [selection, setSelection] = useState<Record<number, RowSelection>>({});
  const [savedOrders, setSavedOrders] = useState<Record<number, SavedOrder>>(
    {}
  );
  const [savingId, setSavingId] = useState<number | null>(null);
  const [isBatchSaving, setIsBatchSaving] = useState(false);
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  const loadRecordAvailability = useCallback(
    async (
      record: HoraCertaQuarantineRecord,
      date: string,
      cargoTypeId: number
    ) => {
      setDocksByRecord((current) => ({ ...current, [record.id]: [] }));
      if (!date) return;

      setLoadingDocks((current) => ({ ...current, [record.id]: true }));
      try {
        const docks = await findAvailability(record.id, date, cargoTypeId);
        setDocksByRecord((current) => ({ ...current, [record.id]: docks }));
      } catch (error) {
        setRowErrors((current) => ({
          ...current,
          [record.id]: getErrorMessage(error),
        }));
      } finally {
        setLoadingDocks((current) => ({ ...current, [record.id]: false }));
      }
    },
    [findAvailability]
  );

  const loadRecordDocks = useCallback(
    (record: HoraCertaQuarantineRecord) => {
      const existing = selectionRef.current[record.id];
      const date = existing?.date ?? getScheduledDate(record.originalScheduledAt);
      const cargoTypeId =
        existing?.cargoTypeId ??
        record.cargoType?.id ??
        (record.cargoTypes.length === 1 ? record.cargoTypes[0].id : undefined);
      const selected = existing ?? { date, cargoTypeId, slot: '' };

      setSelection((current) => ({ ...current, [record.id]: selected }));
      setDocksByRecord((current) => ({ ...current, [record.id]: [] }));

      if (cargoTypeId) {
        void loadRecordAvailability(record, date, cargoTypeId);
      }
    },
    [loadRecordAvailability]
  );

  const loadRecords = useCallback(
    async (targetPage = 1) => {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        const response = await findQuarantine({
          status: 'PENDING',
          page: targetPage,
          limit: pageSize,
          ...dateRange,
        });
        setRecords(response.data);
        setMeta(response.meta);
        setDocksByRecord({});
        setSavedOrders({});
        setRowErrors({});
        response.data.forEach((record) => {
          void loadRecordDocks(record);
        });
      } catch (error) {
        setErrorMessage(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    },
    [findQuarantine, loadRecordDocks, dateRange, pageSize]
  );

  useEffect(() => {
    void loadRecords();
  }, [loadRecords]);

  const getRecordDuration = useCallback(
    (record: HoraCertaQuarantineRecord): number | null => {
      const cargoTypeId = selection[record.id]?.cargoTypeId;
      if (!cargoTypeId) return null;

      const option = record.cargoTypes.find((entry) => entry.id === cargoTypeId);
      if (!option) return null;

      return record.orderType.id === OrderTypeEnum.CARREGAMENTO
        ? option.loadDuration
        : option.unloadDuration;
    },
    [selection]
  );

  const occupiedIntervals = useMemo(() => {
    const intervals: Array<{
      recordId: number;
      dockId: number;
      date: string;
      start: number;
      end: number;
    }> = [];

    records.forEach((record) => {
      const row = selection[record.id];
      const slot = row?.slot ?? savedOrders[record.id]?.slot;
      const date = row?.date ?? getScheduledDate(record.originalScheduledAt);
      const duration = getRecordDuration(record);
      if (!slot || !date || !duration) return;

      const [dockId, timeframe] = slot.split('::');
      const start = parseTimeToMinutes(timeframe);
      if (start === null) return;

      intervals.push({
        recordId: record.id,
        dockId: Number(dockId),
        date,
        start,
        end: start + duration,
      });
    });

    return intervals;
  }, [records, selection, savedOrders, getRecordDuration]);

  const handleSlotChange = useCallback((recordId: number, slot: string) => {
    setSelection((current) => ({
      ...current,
      [recordId]: { ...(current[recordId] ?? { date: '' }), slot },
    }));
  }, []);

  const slotOptions = useCallback(
    (record: HoraCertaQuarantineRecord): SlotOption[] => {
      const docks = docksByRecord[record.id] ?? [];
      const date =
        selection[record.id]?.date ??
        getScheduledDate(record.originalScheduledAt);
      const duration = getRecordDuration(record) ?? record.duration;

      return docks.flatMap((dock) =>
        dock.timeframes.map((timeframe) => {
          const value = `${dock.dock.id}::${timeframe}`;
          const start = parseTimeToMinutes(timeframe);
          const end = start === null ? null : start + duration;
          const taken =
            start !== null &&
            end !== null &&
            Boolean(date) &&
            occupiedIntervals.some(
              (interval) =>
                interval.recordId !== record.id &&
                interval.dockId === dock.dock.id &&
                interval.date === date &&
                start < interval.end &&
                interval.start < end
            );

          return {
            value,
            label: `${dock.dock.name} - ${timeframe}`,
            taken,
          };
        })
      );
    },
    [docksByRecord, selection, getRecordDuration, occupiedIntervals]
  );

  const handleCargoTypeChange = useCallback(
    (recordId: number, cargoTypeId: number) => {
      const record = records.find((entry) => entry.id === recordId);
      if (!record) return;

      const date =
        selection[recordId]?.date ??
        getScheduledDate(record.originalScheduledAt);
      setSelection((current) => ({
        ...current,
        [recordId]: { date, cargoTypeId, slot: '' },
      }));
      void loadRecordAvailability(record, date, cargoTypeId);
    },
    [records, selection, loadRecordAvailability]
  );

  const filledSelections = useMemo(
    () =>
      Object.entries(selection).filter(
        ([, row]) => row.date && row.cargoTypeId && row.slot
      ),
    [selection]
  );

  const saveRecord = useCallback(
    async (record: HoraCertaQuarantineRecord) => {
      const row = selection[record.id];
      if (
        !row?.date ||
        !row.cargoTypeId ||
        !row.slot ||
        savingId !== null ||
        isBatchSaving
      )
        return;

      const confirmed = await confirmationDialogRef.current?.openDialog({
        title: 'Confirmar criação da ordem?',
        message:
          'Ao confirmar, este registro sairá da quarentena e será convertido em uma ordem no Webcol. Após a criação, ele não aparecerá mais nesta lista.',
        confirmButtonText: 'Criar ordem',
      });
      if (!confirmed) return;

      const [dockId, timeframe] = row.slot.split('::');
      const slot = row.slot;

      setSavingId(record.id);
      setErrorMessage(null);
      setRowErrors((current) => {
        const next = { ...current };
        delete next[record.id];
        return next;
      });

      try {
        const response = await confirmQuarantine([
          {
            quarantineId: record.id,
            dockId: Number(dockId),
            cargoTypeId: row.cargoTypeId,
            scheduledAt: `${row.date}T${timeframe}:00-03:00`,
          },
        ]);
        const orderId = response.orderIds?.[0];
        setSavedOrders((current) => ({
          ...current,
          [record.id]: { orderId, slot },
        }));
        setSelection((current) => {
          const next = { ...current };
          Object.entries(next).forEach(([key, value]) => {
            if (Number(key) !== record.id && value.slot === slot) {
              next[Number(key)] = { ...value, slot: '' };
            }
          });
          return next;
        });
        notify('success', `Ordem #${orderId} criada com sucesso.`);
        setSelection((current) => {
          const next = { ...current };
          delete next[record.id];
          return next;
        });
        await loadRecords(meta?.currentPage ?? 1);
      } catch (error) {
        setRowErrors((current) => ({
          ...current,
          [record.id]: getErrorMessage(error),
        }));
      } finally {
        setSavingId(null);
      }
    },
    [
      selection,
      savingId,
      isBatchSaving,
      confirmQuarantine,
      loadRecords,
      meta,
    ]
  );

  const saveBatch = useCallback(async () => {
    if (filledSelections.length === 0 || savingId !== null || isBatchSaving)
      return;

    const confirmed = await confirmationDialogRef.current?.openDialog({
      title: 'Confirmar criação das ordens?',
      message:
        'Ao confirmar, estes registros sairão da quarentena e serão convertidos em ordens no Webcol. Após a criação, eles não aparecerão mais nesta lista.',
      confirmButtonText: 'Criar ordens',
    });
    if (!confirmed) return;

    const items = filledSelections.map(([recordId, row]) => {
      const [dockId, timeframe] = row.slot.split('::');
      return {
        quarantineId: Number(recordId),
        dockId: Number(dockId),
        cargoTypeId: row.cargoTypeId!,
        scheduledAt: `${row.date}T${timeframe}:00-03:00`,
      };
    });

    setIsBatchSaving(true);
    setErrorMessage(null);
    try {
      const response = await confirmQuarantine(items);
      const created = response.created ?? response.orderIds.length;
      const savedIds = new Set(items.map((item) => item.quarantineId));
      setSelection((current) =>
        Object.fromEntries(
          Object.entries(current).filter(
            ([recordId]) => !savedIds.has(Number(recordId))
          )
        )
      );
      notify(
        'success',
        created === 1
          ? '1 ordem criada com sucesso.'
          : `${created} ordens criadas com sucesso.`
      );
      await loadRecords(meta?.currentPage ?? 1);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsBatchSaving(false);
    }
  }, [
    filledSelections,
    savingId,
    isBatchSaving,
    confirmQuarantine,
    loadRecords,
    meta,
  ]);

  const handleExpirePast = useCallback(async () => {
    const confirmed = await confirmationDialogRef.current?.openDialog({
      title: 'Limpar antigos',
      message:
        'Deseja remover da quarentena os registros pendentes com data de agendamento anterior a hoje?',
      confirmButtonText: 'Limpar',
    });
    if (!confirmed) return;

    setIsExpiring(true);
    setErrorMessage(null);
    try {
      const { expired } = await expirePastQuarantine();
      notify(
        'success',
        expired > 0
          ? `${expired} registro(s) antigo(s) removido(s).`
          : 'Nenhum registro antigo encontrado.'
      );
      await loadRecords(meta?.currentPage ?? 1);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsExpiring(false);
    }
  }, [expirePastQuarantine, loadRecords, meta]);

  const handleDeleteRecord = useCallback(
    async (record: HoraCertaQuarantineRecord) => {
      const confirmed = await confirmationDialogRef.current?.openDialog({
        title: 'Excluir da quarentena',
        message: `Deseja excluir o agendamento ${record.thirdOrder}? Essa ação não poderá ser desfeita.`,
        confirmButtonText: 'Excluir',
      });
      if (!confirmed) return;

      setDeletingId(record.id);
      setErrorMessage(null);
      try {
        await deleteQuarantineRecord(record.id);
        notify(
          'success',
          `O agendamento ${record.thirdOrder} foi removido da quarentena.`
        );
        await loadRecords(meta?.currentPage ?? 1);
      } catch (error) {
        setErrorMessage(getErrorMessage(error));
      } finally {
        setDeletingId(null);
      }
    },
    [deleteQuarantineRecord, loadRecords, meta]
  );

  return (
    <Scaffold>
      <ConfirmationDialog ref={confirmationDialogRef} />
      <S.MainPanel as="section">
        <QuarantineHeader />

        <QuarantineDateFilter value={dateFilter} onChange={setDateFilter} />

        <QuarantineTable
          records={records}
          isLoading={isLoading}
          selection={selection}
          savedOrders={savedOrders}
          docksByRecord={docksByRecord}
          loadingDocks={loadingDocks}
          rowErrors={rowErrors}
          savingId={savingId ?? deletingId}
          batchCount={filledSelections.length}
          isBatchSaving={isBatchSaving}
          getSlotOptions={slotOptions}
          getStandardDuration={getRecordDuration}
          onCargoTypeChange={handleCargoTypeChange}
          onSlotChange={handleSlotChange}
          onSave={saveRecord}
          onSaveBatch={() => void saveBatch()}
          onDelete={handleDeleteRecord}
          onReload={() => void loadRecords(meta?.currentPage ?? 1)}
          onExpirePast={() => void handleExpirePast()}
          isExpiring={isExpiring}
        />

        <Paginator
          alwaysVisible
          pagination={
            meta
              ? {
                  currentPage: meta.currentPage,
                  lastPage: meta.lastPage,
                  total: meta.total,
                }
              : undefined
          }
          onPageChange={(nextPage) => void loadRecords(nextPage)}
          trailingContent={
            <label className="ml-4 flex items-center gap-2 text-sm text-slate-600">
              Por página
              <select
                aria-label="Registros por página"
                className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700"
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          }
        />

        {errorMessage && (
          <p role="alert" className="mt-4 text-sm text-red-600">
            {errorMessage}
          </p>
        )}
      </S.MainPanel>
    </Scaffold>
  );
};
